import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of, from, delay, throwError, map, switchMap, catchError } from 'rxjs';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { SupabaseService } from '../services/supabase.service';
import { getCityStats } from './mock-stats-cache';
import { MOCK_DB } from './mock-data';

/* ── Java Backend Token Cache ── */
let _javaToken: string | null = null;
let _javaTokenExpiresAt = 0;

async function ensureJavaToken(): Promise<string> {
  if (_javaToken && Date.now() < _javaTokenExpiresAt) return _javaToken;
  const backendUrl = (environment as any).javaApiUrl;
  const resp = await fetch(`${backendUrl}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: 'admin@axxyl.com', password: 'admin123' }),
  });
  if (!resp.ok) throw new Error('Java backend auth failed');
  const data = await resp.json();
  const tokenData = data.data ?? data;
  _javaToken = tokenData.accessToken ?? tokenData.token;
  const expiresIn = tokenData.expiresIn ?? 900;
  _javaTokenExpiresAt = Date.now() + (expiresIn - 60) * 1000;
  return _javaToken!;
}

const MOCK_USERS: Record<string, any> = {
  'admin@axxyl.com': {
    id: 1, email: 'admin@axxyl.com', firstName: 'Dev', lastName: 'Admin',
    role: 'ADMIN', status: 'ACTIVE', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
  },
  'tech@axxyl.com': {
    id: 2, email: 'tech@axxyl.com', firstName: 'Alex', lastName: 'Tech',
    role: 'TECH', status: 'ACTIVE', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
  },
  'cs@axxyl.com': {
    id: 3, email: 'cs@axxyl.com', firstName: 'Sarah', lastName: 'Support',
    role: 'CUSTOMER_SERVICE', status: 'ACTIVE', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
  },
};
const MOCK_PASSWORD = 'admin123';

/**
 * Patterns for paths that have a Java Swagger endpoint.
 * Checked using original frontend paths (before path-mapper rewrite).
 *
 * Only matches actual API patterns — not sub-resources like
 * /drivers/{id}/trips which the Java API does not support.
 */
const SWAGGER_PATTERNS: RegExp[] = [
  // Ratings always use the real admin reads, including in hybrid mode.
  /^\/ratings(?=\/|$)/,
  /^\/admin\/ratings(?=\/|$)/,
  /^\/admin\/drivers\/\d+\/ratings$/,
  // Auth
  /^\/auth\//,

  // Drivers: list, pending, grace, single, approve
  /^\/drivers$/,
  /^\/drivers\/pending$/,
  /^\/drivers\/grace$/,
  /^\/drivers\/approve$/,
  /^\/drivers\/\d+$/,
  /^\/drivers\/\d+\/billing/,
  /^\/drivers\/\d+\/fee-profile/,

  // Passengers → rewritten to /users
  /^\/passengers$/,
  /^\/passengers\/\d+$/,

  // Trips
  /^\/trips$/,
  /^\/trips\/\d+$/,

  // Promos
  /^\/promos$/,
  /^\/promos\/\d+$/,

  // Categories
  /^\/categories$/,
  /^\/categories\/icon-types$/,
  /^\/categories\/\d+$/,

  // Geo
  /^\/geo\/countries/,
  /^\/geo\/states/,
  /^\/geo\/cities/,

  // Documents (list, add, edit, delete, by-id)
  /^\/documents/,

  // Driver Fees (admin)
  /^\/driver-fees/,

  // Fare Configs (admin)
  /^\/fare-configs/,

  // Geofence / Service Zones
  /^\/service-zones/,

  // Payments (list, detail, driver payments)
  /^\/payments/,

  // Payout
  /^\/payout\//,

  // Profile
  /^\/profile$/,

  // Dashboard stats
  /^\/dashboard\/stats$/,

  // Team management
  /^\/team(?=\/|$)/,

  // Vendors
  /^\/vendors(?=\/|$)/,

  // Activity / Audit log
  /^\/activity\/steps/,
];

function hasSwaggerEndpoint(path: string): boolean {
  return SWAGGER_PATTERNS.some(pattern => pattern.test(path));
}

export const mockApiInterceptor: HttpInterceptorFn = (req, next) => {
  const apiBase = environment.apiBaseUrl;
  if (!req.url.startsWith(apiBase)) return next(req);

  const path = req.url.replace(apiBase, '').split('?')[0];
  const params = req.params;

  // Demo authentication and password flows stay local; they never send real reset emails.
  if (environment.useMockData && (['/auth/login', '/auth/refresh', '/auth/csrf', '/auth/logout',
      '/auth/password-reset/request', '/auth/password-reset/validate', '/auth/password-reset/confirm',
      '/auth/password/change'].includes(path))) {
    return handleMockAuth(path, req);
  }

  // --- Hybrid mode: forward Swagger-backed paths, mock the rest ---

  // If path has a real Java Swagger endpoint, let it through
  if (hasSwaggerEndpoint(path)) {
    return next(req);
  }

  // Dashboard stats — aggregate from mock city stats
  if (path === '/dashboard/stats' && req.method === 'GET') {
    const entries = getCityStats();
    const stats = {
      totalDrivers: entries.reduce((s, e) => s + (e.totalDrivers || 0), 0),
      activeDrivers: entries.reduce((s, e) => s + (e.activeDrivers || 0), 0),
      gracePeriod: entries.reduce((s, e) => s + (e.gracePeriodDrivers || 0), 0),
      totalTrips: entries.reduce((s, e) => s + (e.totalTrips || 0), 0),
      completedTrips: entries.reduce((s, e) => s + (e.completedTrips || 0), 0),
      totalRevenue: entries.reduce((s, e) => s + (e.totalRevenue || 0), 0),
      totalFees: entries.reduce((s, e) => s + (e.totalAirportFees || 0) + (e.totalMiscFees || 0) + (e.totalCityFees || 0), 0),
      commissionRevenue: entries.reduce((s, e) => s + (e.commissionRevenue || 0), 0),
      subscriptionRevenue: entries.reduce((s, e) => s + (e.subscriptionRevenue || 0), 0),
      totalPassengers: entries.reduce((s, e) => s + (e.totalPassengers || 0), 0),
      activePassengers: entries.reduce((s, e) => s + (e.activePassengers || 0), 0),
    };
    return respond(stats);
  }

  // Migration-only: seed endpoint for dashboard city stats
  if (path === '/dashboard/city-stats-seed') {
    const stats = getCityStats();
    return respond(stats.map((s, i) => ({ id: i + 1, ...s })));
  }

  // Migration mode: X-Mock-Source header forces MOCK_DB for seeding
  const isMockSeed = req.headers.get('X-Mock-Source') === 'seed';
  if (isMockSeed) {
    return from(handleMockSeed(path, params));
  }

  // Serve from MOCK_DB if available (covers payroll paths and others)
  const mockBasePath = findMockPath(path);
  if (mockBasePath) {
    if (req.method === 'GET') {
      return from(handleMockCrud(mockBasePath, path, params));
    }
    if (req.method === 'POST') {
      const body = (req.body ?? {}) as Record<string, unknown>;
      return respond({ ...body, id: Date.now(), createdAt: new Date().toISOString() }, 201);
    }
    if (req.method === 'PUT' || req.method === 'PATCH') {
      const body = (req.body ?? {}) as Record<string, unknown>;
      return respond({ ...body, updatedAt: new Date().toISOString() });
    }
    if (req.method === 'DELETE') {
      return respond({ success: true });
    }
  }

  // Supabase fallback
  const supabase = inject(SupabaseService);
  if (supabase.enabled()) {
    return from(handleSupabase(supabase, path, req.method, req.params, req.body));
  }

  // Nothing matched — return empty mock to prevent errors
  if (req.method === 'GET') {
    return respond({ content: [], totalElements: 0, totalPages: 0, size: 20, number: 0 });
  }
  return next(req);
};

/** Find the longest MOCK_DB key that matches the beginning of the path */
function findMockPath(path: string): string | null {
  const sorted = Object.keys(MOCK_DB).sort((a, b) => b.length - a.length);
  return sorted.find(key => path === key || path.startsWith(key + '/')) ?? null;
}

/** Handle GET requests with optional ID extraction and paging from MOCK_DB */
async function handleMockCrud(basePath: string, fullPath: string, params: any): Promise<HttpResponse<any>> {
  const data = MOCK_DB[basePath];
  if (!data) {
    return new HttpResponse({ status: 404, body: { message: `No mock data for ${basePath}` } });
  }

  // Check for ID: e.g. /payments/123
  const suffix = fullPath.substring(basePath.length);
  const idMatch = suffix.match(/^\/(\d+)$/);
  if (idMatch) {
    const id = parseInt(idMatch[1], 10);
    const item = data.find((d: any) => d.id === id);
    return new HttpResponse({ status: item ? 200 : 404, body: item ?? { message: 'Not found' } });
  }

  // Sub-resource: e.g. /passengers/5/trips
  const subMatch = suffix.match(/^\/(\d+)\/([a-z-]+)$/);
  if (subMatch) {
    const parentId = parseInt(subMatch[1], 10);
    const subResource = subMatch[2];
    const subData = MOCK_DB['/' + subResource];
    if (subData) {
      const fkField = basePath === '/passengers' ? 'passengerId' : 'driverId';
      const filtered = subData.filter((d: any) => d[fkField] === parentId);
      return new HttpResponse({ status: 200, body: { content: filtered, totalElements: filtered.length } });
    }
    return new HttpResponse({ status: 200, body: { content: [], totalElements: 0 } });
  }

  // Paged list
  const size = parseInt(params.get('size') ?? '20', 10);
  const page = parseInt(params.get('page') ?? '0', 10);
  const start = page * size;
  const content = data.slice(start, start + size);
  return new HttpResponse({
    status: 200,
    body: { content, totalElements: data.length, totalPages: Math.ceil(data.length / size), size, number: page },
  });
}

function respond(body: any, status = 200) {
  const latency = 50 + Math.random() * 100;
  if (status >= 400) {
    return throwError(() => new HttpErrorResponse({ status, error: body })).pipe(delay(latency));
  }
  return of(new HttpResponse({ status, body })).pipe(delay(latency));
}

async function handleSupabase(
  supabase: SupabaseService,
  path: string,
  method: string,
  params: any,
  body: any,
): Promise<HttpResponse<any>> {
  try {
    // Dashboard stats — aggregate from dashboard_city_stats table
    if (path === '/dashboard/stats') {
      const result = await supabase.getDashboardStats({
        region: params.get('region') || '',
        country: params.get('country') || '',
        state: params.get('state') || '',
        city: params.get('city') || '',
      }, {
        from: params.get('from') || undefined,
        to: params.get('to') || undefined,
      });
      return new HttpResponse({ status: 200, body: result });
    }

    // Stats revenue — query from stats_revenue table
    if (path === '/stats/revenue') {
      const result = await supabase.getAll('stats_revenue');
      return new HttpResponse({ status: 200, body: result });
    }

    // Payment methods regional — query from payment_methods table
    if (path === '/payment-methods/regional' && method === 'GET') {
      const countryCode = params.get('countryCode') || 'US';
      const result = await supabase.getPaymentMethods(countryCode);
      return new HttpResponse({ status: 200, body: result });
    }

    // Sub-resource: /users/:id/trips, /drivers/:id/documents, etc.
    const subMatch = path.match(/^\/([a-z]+)\/(\d+)\/([a-z-]+)$/);
    if (subMatch && method === 'GET') {
      const [, parentResource, parentId, subResource] = subMatch;
      return await handleSubResource(supabase, parentResource, parentId, subResource);
    }
    if (subMatch && method === 'POST') {
      return new HttpResponse({ status: 201, body: { ...body, id: Date.now(), createdAt: new Date().toISOString() } });
    }
    // Sub-resource DELETE: /users/:id/documents/:docId
    const subDelMatch = path.match(/^\/[a-z]+\/\d+\/[a-z-]+\/(\d+)$/);
    if (subDelMatch && method === 'DELETE') {
      return new HttpResponse({ status: 200, body: { success: true } });
    }

    // Standard CRUD
    const resolved = supabase.resolveTable(path);
    if (!resolved) {
      return new HttpResponse({ status: 404, body: { message: 'Not found' } });
    }

    const { table, id, isAirport } = resolved;

    if (method === 'GET' && !id) {
      const paramObj: Record<string, string> = {};
      params.keys().forEach((k: string) => { paramObj[k] = params.get(k); });
      const extraFilters = isAirport ? { airport: 'NOT_NULL' } : undefined;
      const result = await supabase.getPage(table, paramObj, extraFilters);
      return new HttpResponse({ status: 200, body: result });
    }

    if (method === 'GET' && id) {
      const result = await supabase.getById(table, id);
      return new HttpResponse({ status: 200, body: result });
    }

    if (method === 'POST') {
      const result = await supabase.create(table, body);
      return new HttpResponse({ status: 201, body: result });
    }

    if (method === 'PUT' && id) {
      const result = await supabase.update(table, id, body);
      return new HttpResponse({ status: 200, body: result });
    }

    if (method === 'DELETE' && id) {
      const result = await supabase.remove(table, id);
      return new HttpResponse({ status: 200, body: result });
    }

    return new HttpResponse({ status: 404, body: { message: 'Not found' } });
  } catch (err: any) {
    console.error('[Supabase]', err);
    throw new HttpErrorResponse({ status: 500, error: { message: err.message ?? 'Supabase error' } });
  }
}

async function handleSubResource(
  supabase: SupabaseService,
  parentResource: string,
  parentId: string,
  subResource: string,
): Promise<HttpResponse<any>> {
  const fkColumn = parentResource === 'passengers' ? 'passenger_id' : 'driver_id';

  if (subResource === 'fee-history') {
    const result = await supabase.getSubResource(parentResource, parentId, 'fee_history', 'driver_id');
    return new HttpResponse({ status: 200, body: result });
  }

  const subTableMap: Record<string, string> = {
    trips: 'trips',
    payments: parentResource === 'passengers' ? 'payments' : 'driver_payments',
    ratings: 'ratings',
    reviews: 'reviews',
    documents: 'documents',
  };

  const childTable = subTableMap[subResource];
  if (!childTable) {
    return new HttpResponse({ status: 404, body: { message: `Unknown sub-resource: ${subResource}` } });
  }

  const result = await supabase.getSubResource(parentResource, parentId, childTable, fkColumn);
  return new HttpResponse({ status: 200, body: result });
}

async function handleMockSeed(path: string, params: any): Promise<HttpResponse<any>> {
  const size = parseInt(params.get('size') ?? '500', 10);
  const page = parseInt(params.get('page') ?? '0', 10);
  const data = MOCK_DB[path];
  if (!data) {
    return new HttpResponse({ status: 404, body: { message: `No mock data for ${path}` } });
  }
  const start = page * size;
  const content = data.slice(start, start + size);
  return new HttpResponse({
    status: 200,
    body: { content, totalElements: data.length, totalPages: Math.ceil(data.length / size), size, number: page },
  });
}

/* ── Java Backend Route Resolution ── */

interface BackendRouteInfo {
  backendPath: string;
  mapResponse?: (body: any) => any;
  mapRequest?: (body: any) => any;
}

function resolveBackendRoute(path: string): BackendRouteInfo | null {
  // ── Categories ──
  if (path === '/categories/icon-types') {
    return { backendPath: '/api/car-categories/icon-types' };
  }

  const catMatch = path.match(/^\/categories(?:\/(\d+))?$/);
  if (catMatch) {
    const id = catMatch[1];
    return {
      backendPath: id ? `/api/car-categories/${id}` : '/api/car-categories',
      mapResponse: mapCarCategoryResponse,
      mapRequest: mapCarCategoryRequest,
    };
  }

  // ── Passengers admin CRUD ──
  const paxMatch = path.match(/^\/passengers(?:\/(\d+))?$/);
  if (paxMatch) {
    const id = paxMatch[1];
    return {
      backendPath: id ? `/api/users/${id}` : '/api/users',
      mapResponse: mapPassengerResponse,
      mapRequest: mapPassengerRequest,
    };
  }

  // ── Drivers admin CRUD ──
  if (path === '/drivers/pending') {
    return { backendPath: '/api/drivers/pending', mapResponse: mapDriverResponse };
  }
  if (path === '/drivers/grace') {
    return { backendPath: '/api/drivers/grace', mapResponse: mapDriverResponse };
  }
  const drvMatch = path.match(/^\/drivers(?:\/(\d+))?$/);
  if (drvMatch) {
    const id = drvMatch[1];
    return {
      backendPath: id ? `/api/drivers/${id}` : '/api/drivers',
      mapResponse: mapDriverResponse,
      mapRequest: mapDriverRequest,
    };
  }

  // ── Driver Billing ──
  const billingMatch = path.match(/^\/drivers\/(\d+)\/billing$/);
  if (billingMatch) {
    return { backendPath: `/api/drivers/${billingMatch[1]}/billing` };
  }

  // ── Vehicles admin ──
  const vehMatch = path.match(/^\/vehicles(?:\/(\d+))?$/);
  if (vehMatch) {
    const id = vehMatch[1];
    return {
      backendPath: id ? `/api/admin/vehicles/${id}` : '/api/admin/vehicles',
    };
  }

  // ── Fare Configs admin ──
  const fareConfMatch = path.match(/^\/fare-configs(?:\/(\d+))?$/);
  if (fareConfMatch) {
    const id = fareConfMatch[1];
    return {
      backendPath: id ? `/api/admin/fare-configs/${id}` : '/api/admin/fare-configs',
      mapResponse: mapFareConfigResponse,
      mapRequest: mapFareConfigRequest,
    };
  }

  // ── Driver Fees admin ──
  const drvFeeMatch = path.match(/^\/driver-fees(?:\/(\d+))?$/);
  if (drvFeeMatch) {
    const id = drvFeeMatch[1];
    return {
      backendPath: id ? `/api/admin/driver-fees/${id}` : '/api/admin/driver-fees',
      mapResponse: mapDriverFeeResponse,
      mapRequest: mapDriverFeeRequest,
    };
  }

  // ── Promos admin ──
  const promoActiveMatch = path.match(/^\/promos\/(\d+)\/active$/);
  if (promoActiveMatch) {
    return { backendPath: `/api/admin/promos/${promoActiveMatch[1]}/active` };
  }
  const promoMatch = path.match(/^\/promos(?:\/(\d+))?$/);
  if (promoMatch) {
    const id = promoMatch[1];
    return {
      backendPath: id ? `/api/admin/promos/${id}` : '/api/admin/promos',
      mapResponse: mapPromoResponse,
      mapRequest: mapPromoRequest,
    };
  }

  // ── Payment Summary / Ride Payments ──
  const payRideMatch = path.match(/^\/payments\/ride\/(\d+)$/);
  if (payRideMatch) {
    return { backendPath: `/api/payments/ride/${payRideMatch[1]}`, mapResponse: mapPaymentSummaryResponse };
  }
  const payTxnMatch = path.match(/^\/payments\/transaction\/(.+)$/);
  if (payTxnMatch) {
    return { backendPath: `/api/payments/transaction/${payTxnMatch[1]}`, mapResponse: mapPaymentSummaryResponse };
  }

  // ── Geo: Countries / States / Cities ──
  if (path === '/geo/countries') {
    return { backendPath: '/api/admin/countries' };
  }
  if (path === '/geo/states') {
    return { backendPath: '/api/admin/states' };
  }
  if (path === '/geo/cities') {
    return { backendPath: '/api/admin/cities' };
  }

  // ── Trips admin ──
  const tripMatch = path.match(/^\/trips(?:\/(\d+))?$/);
  if (tripMatch) {
    const id = tripMatch[1];
    return {
      backendPath: id ? `/api/admin/trips/${id}` : '/api/admin/trips',
      mapResponse: mapTripResponse,
      mapRequest: id ? undefined : undefined,
    };
  }

  // ── Billing / Payments admin ──
  if (path === '/payments' || path === '/billing-history') {
    return { backendPath: '/api/admin/billing-history', mapResponse: mapBillingHistoryResponse };
  }
  if (path === '/billing-adjustments') {
    return { backendPath: '/api/admin/billing-adjustments' };
  }

  // ── Service Zones (Geofencing) ──
  const szMatch = path.match(/^\/service-zones(?:\/(\d+))?$/);
  if (szMatch) {
    const id = szMatch[1];
    return { backendPath: id ? `/api/geofence/service-zones/${id}` : '/api/geofence/service-zones' };
  }
  if (path === '/service-zones/match') {
    return { backendPath: '/api/geofence/service-zones/match' };
  }

  // ── Dispatch ──
  const dispatchStatusMatch = path.match(/^\/dispatch\/(\d+)\/status$/);
  if (dispatchStatusMatch) {
    return { backendPath: `/api/dispatch/${dispatchStatusMatch[1]}/status` };
  }
  const dispatchAcceptMatch = path.match(/^\/dispatch\/(\d+)\/accept$/);
  if (dispatchAcceptMatch) {
    return { backendPath: `/api/dispatch/${dispatchAcceptMatch[1]}/accept` };
  }
  const dispatchRejectMatch = path.match(/^\/dispatch\/(\d+)\/reject$/);
  if (dispatchRejectMatch) {
    return { backendPath: `/api/dispatch/${dispatchRejectMatch[1]}/reject` };
  }
  if (path === '/dispatch') {
    return { backendPath: '/api/dispatch' };
  }

  // ── Driver Fee Profile & Override ──
  const feeProfileMatch = path.match(/^\/drivers\/(\d+)\/fee-profile$/);
  if (feeProfileMatch) {
    return { backendPath: `/api/drivers/${feeProfileMatch[1]}/fee-profile` };
  }
  const feeOverrideMatch = path.match(/^\/drivers\/(\d+)\/fee-profile\/override$/);
  if (feeOverrideMatch) {
    return { backendPath: `/api/drivers/${feeOverrideMatch[1]}/fee-profile/override` };
  }
  const subActivateMatch = path.match(/^\/drivers\/(\d+)\/billing\/activate-subscription$/);
  if (subActivateMatch) {
    return { backendPath: `/api/drivers/${subActivateMatch[1]}/billing/activate-subscription` };
  }

  // ── Geo Admin CRUD ──
  const geoCountryMatch = path.match(/^\/geo\/countries\/import$/);
  if (geoCountryMatch) {
    return { backendPath: '/api/admin/countries/import' };
  }
  const geoStateMatch = path.match(/^\/geo\/states\/import$/);
  if (geoStateMatch) {
    return { backendPath: '/api/admin/states/import' };
  }
  const geoCityMatch = path.match(/^\/geo\/cities\/import$/);
  if (geoCityMatch) {
    return { backendPath: '/api/admin/cities/import' };
  }
  const geoStateIdMatch = path.match(/^\/geo\/states\/(\d+)$/);
  if (geoStateIdMatch) {
    return { backendPath: `/api/admin/states/${geoStateIdMatch[1]}` };
  }
  const geoCityIdMatch = path.match(/^\/geo\/cities\/(\d+)$/);
  if (geoCityIdMatch) {
    return { backendPath: `/api/admin/cities/${geoCityIdMatch[1]}` };
  }

  // ── Documents ──
  const docMatch = path.match(/^\/documents(?:\/(\d+))?$/);
  if (docMatch) {
    const id = docMatch[1];
    if (id) return { backendPath: `/api/documents/${id}` };
    return { backendPath: '/api/documents/list', mapResponse: mapDocumentListResponse };
  }
  if (path === '/documents/add') {
    return { backendPath: '/api/documents/add' };
  }
  if (path === '/documents/delete') {
    return { backendPath: '/api/documents/delete' };
  }

  // ── Ratings (admin list) ──
  if (path === '/ratings' || path.startsWith('/ratings')) {
    return { backendPath: '/api/admin/ratings' };
  }

  // ── Driver Payments ──
  const drvPayMatch = path.match(/^\/payments\/driver(?:\/(\d+))?$/);
  if (drvPayMatch) {
    const id = drvPayMatch[1];
    if (id) return { backendPath: `/api/payment-summary/driver`, mapResponse: mapPaymentSummaryResponse };
    return { backendPath: '/api/payment-summary/driver', mapResponse: mapDriverPaymentListResponse };
  }

  // ── Payout Details ──
  if (path === '/payout/details' || path === '/payout') {
    return { backendPath: '/api/payout/details' };
  }
  if (path === '/payout/add') {
    return { backendPath: '/api/payout/add' };
  }
  if (path === '/payout/edit') {
    return { backendPath: '/api/payout/edit' };
  }

  // ── Fees by Country ──
  const feesByCountryMatch = path.match(/^\/fees\/([A-Z]{2})$/);
  if (feesByCountryMatch) {
    return { backendPath: `/api/fees/${feesByCountryMatch[1]}` };
  }

  // ── Driver Approval ──
  if (path === '/drivers/approve') {
    return { backendPath: '/auth/approve' };
  }

  // ── Driver Availability ──
  if (path === '/drivers/availability') {
    return { backendPath: '/auth/availability' };
  }

  // ── Axxyl Fees (legacy) ──
  if (path === '/axxyl-fees') {
    return { backendPath: '/api/axxyl-fees/get-fees', mapResponse: mapFeesResponse };
  }

  // ── Fare details ──
  const fareDetMatch = path.match(/^\/fare-details\/(.+)$/);
  if (fareDetMatch) {
    return { backendPath: `/api/ride-operations/fare-details/${fareDetMatch[1]}` };
  }

  // ── Dashboard Stats ──
  if (path === '/dashboard/stats') {
    return { backendPath: '/api/admin/dashboard/stats' };
  }
  if (path === '/dashboard/city-stats') {
    return { backendPath: '/api/admin/dashboard/city-stats' };
  }

  return null;
}

function unwrapDTO(body: any): any {
  if (body && typeof body === 'object' && 'status' in body && 'data' in body && body.data !== null) {
    return body.data;
  }
  return body;
}

/* ── Car Category Field Mapping ── */

function mapOneCategory(c: any): any {
  // Mirror the real CarCategoryDTO shape: a category is only a vehicle class
  // (name/seats/carTypeIcon/active) scoped to a country and optional state.
  // All pricing lives in fare_config, not here.
  const isoCode = c.country?.isoCode ?? c.countryIsoCode ?? (typeof c.country === 'string' ? c.country : 'US');
  const countryName = c.country?.name ?? c.countryName ?? isoCode;
  return {
    id: c.id,
    name: c.name,
    seats: c.seats ?? c.numberOfSeats ?? 4,
    carTypeIcon: c.carTypeIcon ?? '',
    active: c.active ?? c.is_active ?? false,
    countryId: c.country?.id ?? c.countryId ?? null,
    countryIsoCode: isoCode,
    countryName,
    stateId: c.state?.id ?? c.stateId ?? null,
    stateName: c.state?.name ?? c.stateName ?? null,
    country: c.country && typeof c.country === 'object'
      ? c.country
      : { id: c.countryId ?? null, isoCode, name: countryName, active: true, rideServiceEnabled: true },
    state: c.state ?? null,
  };
}

function mapCarCategoryResponse(body: any): any {
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOneCategory) };
  }
  if (body && body.id) {
    return mapOneCategory(body);
  }
  return body;
}

function mapCarCategoryRequest(body: any): any {
  if (!body) return body;
  // Forward the real CarCategoryRequestDTO fields only.
  const out: Record<string, unknown> = {};
  const country = body.countryIsoCode ?? body.country ?? body.countryIdentifier;
  if (country != null) out['countryIsoCode'] = country;
  if (body.stateId != null) out['stateId'] = body.stateId;
  if (body.clearState != null) out['clearState'] = body.clearState;
  if (body.name != null) out['name'] = body.name;
  if (body.seats != null) out['seats'] = body.seats;
  if (body.carTypeIcon != null) out['carTypeIcon'] = body.carTypeIcon;
  if (body.active != null) out['active'] = body.active;
  return out;
}

/* ── Passenger Field Mapping ── */

function mapOnePassenger(p: any): any {
  return {
    ...p,
    name: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
    totalPaid: p.totalPaid ?? 0,
    totalTips: p.totalTips ?? 0,
    dob: p.dob ?? null,
    idCardNumber: p.idCardNumber ?? null,
    wheelchairRequired: p.wheelchairRequired ?? p.hasWheelchair ?? false,
    address: p.address ?? null,
  };
}

function mapPassengerResponse(body: any): any {
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOnePassenger) };
  }
  if (body && body.id) return mapOnePassenger(body);
  return body;
}

function mapPassengerRequest(body: any): any {
  if (!body) return body;
  return {
    firstName: body.firstName,
    lastName: body.lastName,
    email: body.email,
    phone: body.phone,
    status: body.status,
    avatar: body.avatar ?? body.profilePhoto,
    password: body.password,
  };
}

/* ── Driver Field Mapping ── */

function mapOneDriver(d: any): any {
  const nameParts = (d.name || '').split(' ');
  return {
    ...d,
    firstName: d.firstName ?? nameParts[0] ?? '',
    lastName: d.lastName ?? nameParts.slice(1).join(' ') ?? '',
    serviceType: d.feeType === 'SUBSCRIPTION' ? 'SUBSCRIPTION' : 'COMMISSION',
    feeType: d.feeType ?? d.billingModel ?? 'COMMISSION',
  };
}

function mapDriverResponse(body: any): any {
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOneDriver) };
  }
  if (body && body.id) return mapOneDriver(body);
  return body;
}

function mapDriverRequest(body: any): any {
  if (!body) return body;
  return {
    firstName: body.firstName,
    lastName: body.lastName,
    email: body.email,
    phone: body.phone,
    status: body.status,
    countryIsoCode: body.countryCode ?? body.countryIsoCode,
    avatar: body.avatar ?? body.profilePhoto,
    approvalStatus: body.approvalStatus,
    feeConfigId: body.feeConfigId,
    graceStartDate: body.graceStartDate,
    graceEndDate: body.graceEndDate,
    graceStatus: body.graceStatus,
    rejectionReason: body.rejectionReason,
  };
}

/* ── Fare Config Field Mapping ── */

function mapOneFareConfig(fc: any): any {
  return {
    ...fc,
    country: fc.countryName,
    countryCode: fc.countryIsoCode,
    state: fc.stateName,
    category: fc.carCategoryName,
    categoryId: fc.carCategoryId,
  };
}

function mapFareConfigResponse(body: any): any {
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOneFareConfig) };
  }
  if (body && body.id) return mapOneFareConfig(body);
  return body;
}

function mapFareConfigRequest(body: any): any {
  if (!body) return body;
  return {
    countryIsoCode: body.countryCode ?? body.countryIsoCode,
    stateId: body.stateId,
    carCategoryId: body.categoryId ?? body.carCategoryId,
    baseFare: body.baseFare,
    perMileRate: body.perMileRate ?? body.perMile,
    perMinuteRate: body.perMinuteRate ?? body.perMinute,
    minimumFare: body.minimumFare,
    waitingFeePerMinute: body.waitingFeePerMinute ?? 0,
    active: body.active ?? true,
  };
}

/* ── Driver Fee Config Field Mapping ── */

function mapOneDriverFee(df: any): any {
  return {
    ...df,
    country: df.countryName,
    countryCode: df.countryIsoCode,
    currency: df.currencyCode,
    chargeMode: df.billingModel?.toLowerCase() ?? 'commission',
    commission: df.commissionPercent,
    daily: df.subscriptionPlan === 'DAILY' ? df.amount : null,
    weekly: df.subscriptionPlan === 'WEEKLY' ? df.amount : null,
    biweekly: df.subscriptionPlan === 'BIWEEKLY' ? df.amount : null,
    monthly: df.subscriptionPlan === 'MONTHLY' ? df.amount : null,
  };
}

function mapDriverFeeResponse(body: any): any {
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOneDriverFee) };
  }
  if (body && body.id) return mapOneDriverFee(body);
  return body;
}

function mapDriverFeeRequest(body: any): any {
  if (!body) return body;
  return {
    countryIsoCode: body.countryCode ?? body.countryIsoCode,
    displayName: body.displayName ?? body.name,
    billingModel: (body.chargeMode ?? body.billingModel ?? 'COMMISSION').toUpperCase(),
    subscriptionPlan: body.subscriptionPlan ?? 'MONTHLY',
    amount: body.amount ?? body.daily ?? body.weekly ?? body.biweekly ?? body.monthly ?? 0,
    commissionPercent: body.commission ?? body.commissionPercent ?? 0,
    active: body.active ?? true,
  };
}

/* ── Promo Campaign Field Mapping ── */

const BENEFIT_TYPE_MAP: Record<string, string> = {
  PERCENT_OFF: 'PERCENTAGE', FLAT_OFF: 'FIXED',
  DRIVER_BONUS: 'DRIVER_BONUS', COMMISSION_WAIVER: 'COMMISSION_WAIVER',
};
const BENEFIT_TYPE_REV: Record<string, string> = {
  PERCENTAGE: 'PERCENT_OFF', FIXED: 'FLAT_OFF',
  DRIVER_BONUS: 'DRIVER_BONUS', COMMISSION_WAIVER: 'COMMISSION_WAIVER',
};

function mapOnePromo(p: any): any {
  const frontType = BENEFIT_TYPE_MAP[p.benefitType] ?? p.benefitType;
  return {
    ...p,
    type: frontType,
    discountType: frontType,
    value: p.benefitValue,
    discountValue: p.benefitValue,
    userAllowed: p.maxUsesPerUser ?? 0,
    maxUsage: p.maxTotalUses,
    maxUsagePerUser: p.maxUsesPerUser,
    expiresAt: p.endsAt,
    status: p.active ? 'ACTIVE' : 'INACTIVE',
    title: p.code,
    country: p.countryIsoCode,
    categoryName: p.carCategoryName,
  };
}

function mapPromoResponse(body: any): any {
  if (Array.isArray(body)) {
    const mapped = body.map(mapOnePromo);
    return { content: mapped, totalElements: mapped.length, totalPages: 1, size: mapped.length, number: 0 };
  }
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOnePromo) };
  }
  if (body && body.id) return mapOnePromo(body);
  return body;
}

function mapPromoRequest(body: any): any {
  if (!body) return body;
  const rawType = body.discountType ?? body.type ?? body.benefitType ?? 'PERCENT_OFF';
  return {
    code: body.code,
    audience: body.audience ?? 'PASSENGER',
    benefitType: BENEFIT_TYPE_REV[rawType] ?? rawType,
    benefitValue: body.discountValue ?? body.value ?? body.benefitValue ?? 0,
    maxBenefitAmount: body.maxBenefitAmount ?? 0,
    minFareAmount: body.minFareAmount ?? 0,
    carCategoryId: body.carCategoryId,
    countryId: body.countryId,
    startsAt: body.startsAt,
    endsAt: body.expiresAt ?? body.endsAt,
    maxTotalUses: body.maxUsage ?? body.maxTotalUses ?? 0,
    maxUsesPerUser: body.maxUsagePerUser ?? body.maxUsesPerUser ?? 1,
    active: body.status === 'ACTIVE' || body.active === true,
  };
}

/* ── Payment Summary Field Mapping ── */

function mapPaymentSummaryResponse(body: any): any {
  if (Array.isArray(body)) return body;
  if (!body) return body;
  return {
    ...body,
    passengerName: body.customerName,
    passengerId: body.customerId,
    driverName: body.driverName,
    pickup: body.pickupLocation,
    dropoff: body.dropoffLocation,
    fare: body.subtotal,
    tip: body.driverTip,
    total: body.totalAmount,
    status: body.paymentStatus,
    amount: body.totalAmount,
  };
}

/* ── Trip Admin Field Mapping ── */

function mapOneTrip(t: any): any {
  return {
    ...t,
    tripId: t.id,
    country: t.zone?.countryIsoCode ?? '',
    city: t.zone?.serviceZoneName ?? '',
    waitingZone: t.zone?.geofenceName ?? '',
    pickupTime: t.createdAt,
    dropoffTime: t.completedAt,
    tip: t.payment?.tip ?? 0,
    total: t.payment?.totalAmount ?? t.fare,
    rating: null,
    baseFare: t.payment?.fareAmount ?? t.fare,
    airportFee: 0,
    transactionId: t.payment?.transactionId ?? '',
    paymentMethod: t.payment?.paymentMethod ?? '',
    paymentStatus: t.payment?.status ?? '',
    cardLast4: t.payment?.maskedCard ?? '',
    commission: t.payment?.commission ?? 0,
    driverEarnings: t.payment?.driverEarnings ?? 0,
    promoCode: t.promoCode ?? '',
    promoDiscount: t.promoDiscount ?? 0,
    cancellation: t.cancellation ?? null,
    stops: t.stops ?? [],
  };
}

function mapTripResponse(body: any): any {
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOneTrip) };
  }
  if (body && body.id) return mapOneTrip(body);
  return body;
}

/* ── Billing History Field Mapping ── */

function mapOneBillingEntry(b: any): any {
  return {
    ...b,
    tripId: b.rideRequestId,
    amount: b.totalAmount,
    fare: b.fareAmount,
    cardLast4: b.maskedCard ?? '',
    cardBrand: '',
    airportFee: 0,
    miscFee: 0,
  };
}

function mapBillingHistoryResponse(body: any): any {
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOneBillingEntry) };
  }
  if (body && body.id) return mapOneBillingEntry(body);
  return body;
}

/* ── Axxyl Fees Mapping ── */

function mapFeesResponse(body: any): any {
  if (Array.isArray(body)) {
    return {
      content: body.map((f: any) => ({
        id: f.id,
        feeType: f.feeType,
        name: f.feeType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
        amount: f.amount,
        active: f.active,
        status: f.active ? 'ACTIVE' : 'INACTIVE',
      })),
      totalElements: body.length,
      totalPages: 1,
      size: body.length,
      number: 0,
    };
  }
  return body;
}

/* ── Auth Mapping ── */

function mapAuthResponse(body: any): any {
  if (!body) return body;
  return {
    accessToken: body.accessToken ?? body.token,
    refreshToken: body.refreshToken,
    expiresIn: body.expiresIn ?? 900,
    user: body.user ?? {
      id: body.userId ?? body.id,
      email: body.email,
      firstName: body.firstName,
      lastName: body.lastName,
      role: body.role ?? 'ADMIN',
      status: body.status ?? 'ACTIVE',
    },
  };
}

function handleMockAuth(path: string, req: any): Observable<HttpEvent<unknown>> {
  const mockSessionId = '11111111-1111-4111-8111-111111111111';
  if (path === '/auth/csrf') return respond({ csrfToken: 'demo-csrf' });
  if (path === '/auth/logout') return respond(null);
  if (path === '/auth/login' && req.method === 'POST') {
    const body = req.body as any;
    const mockUser = MOCK_USERS[body?.email];
    if (mockUser && body?.password === MOCK_PASSWORD) {
      return respond({
        accessToken: 'mock-jwt-token-' + Date.now(),
        refreshToken: 'mock-refresh-token-' + Date.now(),
        user: mockUser,
        sessionId: mockSessionId,
      });
    }
    return respond({ message: 'Invalid email or password' }, 401);
  }
  if (path === '/auth/refresh' && req.method === 'POST') {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('current_user') : null;
    const user = stored ? JSON.parse(stored) : MOCK_USERS['admin@axxyl.com'];
    return respond({
      accessToken: 'mock-jwt-token-' + Date.now(),
      refreshToken: 'mock-refresh-token-' + Date.now(),
      user,
      sessionId: mockSessionId,
    });
  }
  if (req.method === 'POST' && path === '/auth/password-reset/request') return respond(null);
  if (req.method === 'POST' && path.startsWith('/auth/password-reset/')) {
    if (req.body?.userType !== 'staff' || !/^[A-Za-z0-9_-]{43}$/.test(req.body?.token ?? '')) {
      return respond({ code: 'PASSWORD_RESET_INVALID', message: 'This password reset link has expired or is no longer valid.' }, 400);
    }
    if (path === '/auth/password-reset/validate') return respond({ userType: 'staff', expiresAt: new Date(Date.now() + 300_000).toISOString() });
    if (path === '/auth/password-reset/confirm') return respond(null);
  }
  if (req.method === 'POST' && path === '/auth/password/change') {
    return respond({ accessToken: 'mock-jwt-token-' + Date.now(), sessionId: mockSessionId, passwordChangeRequired: false });
  }
  return respond({ message: 'Not found' }, 404);
}

/* ── Document Field Mapping ── */

function mapOneDocument(d: any): any {
  return {
    id: d.id,
    driverId: d.driverId,
    name: d.documentType ?? d.name,
    type: d.documentType,
    fileUrl: d.fileUrl ?? d.url,
    url: d.fileUrl ?? d.url,
    status: d.status ?? 'PENDING',
    uploadedAt: d.createdAt ?? d.uploadedAt,
    expiresAt: d.expiresAt ?? null,
  };
}

function mapDocumentListResponse(body: any): any {
  if (Array.isArray(body)) {
    const mapped = body.map(mapOneDocument);
    return { content: mapped, totalElements: mapped.length, totalPages: 1, size: mapped.length, number: 0 };
  }
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOneDocument) };
  }
  if (body && body.id) return mapOneDocument(body);
  return body;
}

/* ── Driver Payment List Mapping ── */

function mapOneDriverPayment(p: any): any {
  return {
    ...p,
    driverName: p.driverName ?? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
    totalEarnings: p.totalEarnings ?? p.grossAmount ?? 0,
    totalCommission: p.totalCommission ?? p.commissionAmount ?? 0,
    totalTips: p.totalTips ?? p.tipsAmount ?? 0,
    netPayout: p.netPayout ?? p.netAmount ?? 0,
    totalTrips: p.totalTrips ?? p.tripCount ?? 0,
    paymentStatus: p.paymentStatus ?? p.status ?? 'PENDING',
    period: p.period ?? p.paymentPeriod,
  };
}

function mapDriverPaymentListResponse(body: any): any {
  if (Array.isArray(body)) {
    const mapped = body.map(mapOneDriverPayment);
    return { content: mapped, totalElements: mapped.length, totalPages: 1, size: mapped.length, number: 0 };
  }
  if (body?.content && Array.isArray(body.content)) {
    return { ...body, content: body.content.map(mapOneDriverPayment) };
  }
  if (body && body.id) return mapOneDriverPayment(body);
  return body;
}
